import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AppColors } from "../../../util/AppColors";
import {
  normalizeFileUrl,
  urlToPrintableSrc,
} from "../../../util/printableImage";

const isPdfFile = (url = "") => {
  try {
    const value = String(url ?? "").toLowerCase();
    return value.includes(".pdf") || value.includes("application/pdf");
  } catch {
    return false;
  }
};

const FileView = ({ files }) => {
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const fileGroups = useMemo(() => {
    const source = files ?? reqDet?.files;
    return Array.isArray(source) ? source.filter(Boolean) : [];
  }, [files, reqDet?.files]);

  const [printableSrcByUrl, setPrintableSrcByUrl] = useState({});

  const imageUrls = useMemo(() => {
    try {
      const urls = [];
      fileGroups.forEach((frm) => {
        const nested = Array.isArray(frm?.files) ? frm.files : [];
        nested.forEach((fle) => {
          const fileUrl = normalizeFileUrl(fle);
          if (fileUrl && !isPdfFile(fileUrl)) urls.push(fileUrl);
        });
      });
      return [...new Set(urls)];
    } catch {
      return [];
    }
  }, [fileGroups]);

  const imageUrlsKey = useMemo(() => JSON.stringify(imageUrls), [imageUrls]);

  useEffect(() => {
    let cancelled = false;
    let urls = [];
    try {
      urls = JSON.parse(imageUrlsKey);
      if (!Array.isArray(urls)) urls = [];
    } catch {
      urls = [];
    }

    const loadImages = async () => {
      try {
        const settled = await Promise.allSettled(
          urls.map(async (url) => {
            try {
              const printable = await urlToPrintableSrc(url);
              return [url, printable || url];
            } catch {
              return [url, url];
            }
          }),
        );
        if (cancelled) return;

        setPrintableSrcByUrl((prev) => {
          const next = { ...(prev ?? {}) };
          settled.forEach((result) => {
            if (result?.status !== "fulfilled" || !result?.value) return;
            const [url, printable] = result.value;
            if (url && printable) next[url] = printable;
          });
          return next;
        });
      } catch {
        // Never let image preload crash the screen.
      }
    };

    if (urls.length > 0) {
      loadImages();
    }

    return () => {
      cancelled = true;
    };
  }, [imageUrlsKey]);

  if (!fileGroups.length) return null;

  return (
    <div className="ph20 file-view-root">
      {fileGroups.map((frm, i) => {
        const nestedFiles = Array.isArray(frm?.files) ? frm.files : [];
        return (
          <div className="mt5" key={frm?.fileId ?? `file-group-${i}`}>
            {frm?.name ? (
              <Typography className="fs12 blue-color font-medium">
                {frm.name}
              </Typography>
            ) : null}
            {nestedFiles.map((fle, fileIndex) => {
              try {
                const fileUrl = normalizeFileUrl(fle);
                if (!fileUrl) return null;
                const isPdf = isPdfFile(fileUrl);
                const imageSrc = printableSrcByUrl?.[fileUrl] || fileUrl;

                return (
                  <div
                    className="flex-row-ali-cen mt5 file-view-item"
                    key={`${fileUrl}-${fileIndex}`}
                  >
                    {isPdf ? (
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor"
                      >
                        <PictureAsPdfIcon
                          className="imghw40"
                          sx={{ color: AppColors.redBtn }}
                        />
                      </a>
                    ) : (
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor file-view-link"
                      >
                        <img
                          src={imageSrc}
                          className="br5 file-view-image"
                          alt={frm?.name ?? "file"}
                          loading="eager"
                          decoding="async"
                          onError={(e) => {
                            try {
                              if (
                                e?.currentTarget &&
                                fileUrl &&
                                e.currentTarget.src !== fileUrl
                              ) {
                                e.currentTarget.src = fileUrl;
                              }
                            } catch {
                              // ignore img fallback errors
                            }
                          }}
                        />
                      </a>
                    )}
                  </div>
                );
              } catch {
                return null;
              }
            })}
          </div>
        );
      })}
    </div>
  );
};

export default FileView;
